import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { UserPlus, Check, X, Loader2, User, Mail, Phone } from 'lucide-react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export default function AdminJoinRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const loadRequests = async () => {
    try {
      const data = await api.getPendingJoinRequests();
      setRequests(data.pending_requests || []);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleAction = async (userId, action) => {
    setProcessing(userId);
    try {
      await api.handleJoinRequest(userId, action);
      loadRequests();
    } catch (error) {
      alert(error.message || `Failed to ${action} request`);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-join-requests-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Join Requests</h1>
          <p className="text-slate-500">Approve or reject users who want to join your organization</p>
        </div>

        {/* Requests List */}
        {requests.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              <UserPlus className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No pending join requests</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-700" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800">{request.name}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {request.email}
                        </span>
                        {request.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {request.phone}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          {request.role}
                        </span>
                        <span className="text-xs text-slate-400">
                          Requested {request.created_at && format(new Date(request.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAction(request.id, 'approve')}
                        disabled={processing === request.id}
                        className="bg-green-600 hover:bg-green-700 gap-1"
                      >
                        {processing === request.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleAction(request.id, 'reject')}
                        disabled={processing === request.id}
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 gap-1"
                      >
                        <X className="w-4 h-4" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}